import { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  Star,
  Download,
  BarChart3,
  Activity,
  Award,
  Target,
  Zap,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Vendor } from '../types/vendor';

interface VendorPerformanceDashboardProps {
  vendor: Vendor;
}

interface PerformanceMetrics {
  // Job Metrics
  totalJobs: number;
  completedJobs: number;
  inProgressJobs: number;
  cancelledJobs: number;
  completionRate: number; // percentage
  
  // Time Metrics
  averageResponseTime: number; // hours
  averageCompletionTime: number; // hours
  onTimeDeliveryRate: number; // percentage
  
  // Quality Metrics
  averageRating: number; // 0-5
  totalReviews: number;
  qualityScore: number; // 0-100
  repeatBusinessRate: number; // percentage
  
  // Financial Metrics
  totalRevenue: number;
  averageJobValue: number;
  outstandingBalance: number;
  paymentTimeliness: number; // 0-100
  
  // Efficiency Metrics
  utilizationRate: number; // percentage
  productivityScore: number; // 0-100
  costEfficiency: number; // 0-100
}

interface TimeSeriesData {
  date: string;
  jobs: number;
  revenue: number;
  rating: number;
}

interface ServiceBreakdown {
  serviceType: string;
  count: number;
  revenue: number;
  averageRating: number;
}

export default function VendorPerformanceDashboard({
  vendor,
}: VendorPerformanceDashboardProps) {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [serviceBreakdown, setServiceBreakdown] = useState<ServiceBreakdown[]>([]);

  useEffect(() => {
    loadPerformanceData();
  }, [vendor.id, timeRange]);

  const loadPerformanceData = async () => {
    try {
      setIsLoading(true);
      
      // In production, this would fetch from your API
      // For now, we'll use the vendor's existing performance metrics
      const mockMetrics: PerformanceMetrics = {
        totalJobs: vendor.performanceMetrics.jobsCompleted + vendor.performanceMetrics.jobsInProgress,
        completedJobs: vendor.performanceMetrics.jobsCompleted,
        inProgressJobs: vendor.performanceMetrics.jobsInProgress,
        cancelledJobs: 0,
        completionRate: vendor.performanceMetrics.jobsCompleted > 0 
          ? (vendor.performanceMetrics.jobsCompleted / (vendor.performanceMetrics.jobsCompleted + vendor.performanceMetrics.jobsInProgress)) * 100 
          : 0,
        averageResponseTime: 2.5,
        averageCompletionTime: 8.3,
        onTimeDeliveryRate: vendor.performanceMetrics.onTimeCompletionRate,
        averageRating: vendor.rating?.overall || 0,
        totalReviews: vendor.rating?.totalReviews || 0,
        qualityScore: (vendor.rating?.overall || 0) * 20, // Convert 0-5 to 0-100
        repeatBusinessRate: 75,
        totalRevenue: vendor.performanceMetrics.totalRevenue,
        averageJobValue: vendor.performanceMetrics.averageInvoiceAmount,
        outstandingBalance: 0,
        paymentTimeliness: 95,
        utilizationRate: 78,
        productivityScore: 85,
        costEfficiency: 88,
      };

      // Mock time series data
      const mockTimeSeries: TimeSeriesData[] = generateMockTimeSeries(timeRange);
      
      // Mock service breakdown
      const mockServices: ServiceBreakdown[] = [
        { serviceType: 'Pump Repair', count: 45, revenue: 22500, averageRating: 4.8 },
        { serviceType: 'Maintenance', count: 38, revenue: 15200, averageRating: 4.6 },
        { serviceType: 'Emergency Service', count: 12, revenue: 9600, averageRating: 4.9 },
        { serviceType: 'Installation', count: 8, revenue: 12000, averageRating: 4.7 },
      ];

      setMetrics(mockMetrics);
      setTimeSeriesData(mockTimeSeries);
      setServiceBreakdown(mockServices);
      
    } catch (error) {
      console.error('Error loading performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setIsLoading(false);
    }
  };

  const generateMockTimeSeries = (range: string): TimeSeriesData[] => {
    const points = range === 'week' ? 7 : range === 'month' ? 30 : range === 'quarter' ? 90 : 365;
    const data: TimeSeriesData[] = [];
    
    for (let i = points; i > 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        jobs: Math.floor(Math.random() * 5) + 1,
        revenue: Math.floor(Math.random() * 3000) + 500,
        rating: Math.random() * 1 + 4, // 4.0 - 5.0
      });
    }
    
    return data;
  };

  const exportPerformanceReport = async () => {
    try {
      // In production, this would generate a PDF or Excel report
      toast.success('Performance report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const getPerformanceGrade = (score: number): { grade: string; color: string; label: string } => {
    if (score >= 90) return { grade: 'A+', color: 'text-green-600', label: 'Excellent' };
    if (score >= 80) return { grade: 'A', color: 'text-green-500', label: 'Very Good' };
    if (score >= 70) return { grade: 'B', color: 'text-blue-500', label: 'Good' };
    if (score >= 60) return { grade: 'C', color: 'text-yellow-500', label: 'Fair' };
    return { grade: 'D', color: 'text-red-500', label: 'Needs Improvement' };
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No performance data available</p>
      </div>
    );
  }

  const overallScore = Math.round(
    (metrics.completionRate * 0.25) +
    (metrics.qualityScore * 0.30) +
    (metrics.onTimeDeliveryRate * 0.25) +
    (metrics.productivityScore * 0.20)
  );

  const performanceGrade = getPerformanceGrade(overallScore);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Performance Dashboard</h2>
          <p className="text-gray-500 mt-1">{vendor.displayName}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          
          <Button onClick={exportPerformanceReport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Overall Performance Score */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${performanceGrade.color}`}>
                    {performanceGrade.grade}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Overall</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{overallScore}%</h3>
                <p className="text-gray-600 font-medium">{performanceGrade.label} Performance</p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="flex items-center text-green-600">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    +5% vs last period
                  </span>
                  <span className="text-gray-500">
                    Based on {metrics.totalJobs} total jobs
                  </span>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium mb-2">
                <Award className="w-4 h-4 mr-1" />
                Top Performer
              </div>
              <p className="text-sm text-gray-600">Ranked #3 of 42 vendors</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Completion Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <Badge variant={metrics.completionRate >= 90 ? 'default' : 'secondary'}>
                {metrics.completionRate >= 90 ? 'Excellent' : 'Good'}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">{metrics.completionRate.toFixed(1)}%</div>
            <p className="text-sm text-gray-500 mt-1">Completion Rate</p>
            <p className="text-xs text-gray-400 mt-2">
              {metrics.completedJobs} of {metrics.totalJobs} jobs
            </p>
          </CardContent>
        </Card>

        {/* Response Time */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <Badge variant={metrics.averageResponseTime <= 4 ? 'default' : 'secondary'}>
                {metrics.averageResponseTime <= 4 ? 'Fast' : 'Normal'}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">{metrics.averageResponseTime.toFixed(1)}h</div>
            <p className="text-sm text-gray-500 mt-1">Avg Response Time</p>
            <p className="text-xs text-gray-400 mt-2">
              Target: &lt;4 hours
            </p>
          </CardContent>
        </Card>

        {/* Quality Rating */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <Badge variant={metrics.averageRating >= 4.5 ? 'default' : 'secondary'}>
                {metrics.averageRating >= 4.5 ? '5-Star' : 'Rated'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-gray-900">{metrics.averageRating.toFixed(1)}</div>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= metrics.averageRating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">Quality Rating</p>
            <p className="text-xs text-gray-400 mt-2">
              {metrics.totalReviews} reviews
            </p>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <Badge variant="default">
                ${metrics.averageJobValue.toLocaleString()}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              ${metrics.totalRevenue.toLocaleString()}
            </div>
            <p className="text-sm text-gray-500 mt-1">Total Revenue</p>
            <p className="text-xs text-gray-400 mt-2">
              Avg: ${metrics.averageJobValue.toLocaleString()}/job
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Performance Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: any) => [`$${value.toLocaleString()}`, 'Revenue']}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Revenue Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${value}`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: any) => [`$${value.toLocaleString()}`, 'Revenue']}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Service Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Service Types Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Service Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={serviceBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="serviceType" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Bar dataKey="count" fill="#3b82f6" name="Jobs" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Service Revenue */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Revenue by Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ serviceType, percent }) => `${serviceType} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="revenue"
                >
                  {serviceBreakdown.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => `$${value.toLocaleString()}`}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Detailed Performance Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Time Metrics */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Time Efficiency</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Response Time</span>
                    <span className="font-medium">{metrics.averageResponseTime.toFixed(1)}h</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((4 / metrics.averageResponseTime) * 100, 100)}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Completion Time</span>
                    <span className="font-medium">{metrics.averageCompletionTime.toFixed(1)}h</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${Math.min((12 / metrics.averageCompletionTime) * 100, 100)}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">On-Time Delivery</span>
                    <span className="font-medium">{metrics.onTimeDeliveryRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${metrics.onTimeDeliveryRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Quality Metrics */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Quality Scores</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Overall Quality</span>
                    <span className="font-medium">{metrics.qualityScore}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-600 h-2 rounded-full" 
                      style={{ width: `${metrics.qualityScore}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Customer Satisfaction</span>
                    <span className="font-medium">{vendor.performanceMetrics.customerSatisfaction}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${vendor.performanceMetrics.customerSatisfaction}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Repeat Business</span>
                    <span className="font-medium">{metrics.repeatBusinessRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${metrics.repeatBusinessRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Efficiency Metrics */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Efficiency Scores</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Utilization Rate</span>
                    <span className="font-medium">{metrics.utilizationRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full" 
                      style={{ width: `${metrics.utilizationRate}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Productivity Score</span>
                    <span className="font-medium">{metrics.productivityScore}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${metrics.productivityScore}%` }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Cost Efficiency</span>
                    <span className="font-medium">{metrics.costEfficiency}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${metrics.costEfficiency}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Performance Insights & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {metrics.completionRate >= 95 && (
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900">Excellent Completion Rate</h4>
                  <p className="text-sm text-green-700 mt-1">
                    This vendor maintains an outstanding {metrics.completionRate.toFixed(1)}% job completion rate, 
                    significantly above the industry average of 85%.
                  </p>
                </div>
              </div>
            )}
            
            {metrics.averageResponseTime > 4 && (
              <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900">Response Time Could Improve</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Average response time of {metrics.averageResponseTime.toFixed(1)} hours exceeds the 4-hour target. 
                    Consider discussing faster response protocols for emergency jobs.
                  </p>
                </div>
              </div>
            )}
            
            {metrics.averageRating >= 4.8 && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Star className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900">Exceptional Quality Ratings</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    With a {metrics.averageRating.toFixed(1)}/5.0 rating from {metrics.totalReviews} reviews, 
                    this vendor consistently delivers high-quality work that exceeds expectations.
                  </p>
                </div>
              </div>
            )}
            
            {metrics.totalRevenue > 50000 && (
              <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <DollarSign className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-purple-900">High-Value Partnership</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    With ${metrics.totalRevenue.toLocaleString()} in total revenue, this vendor is a key partner. 
                    Consider negotiating volume discounts or preferred service agreements.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
